(() => {
  'use strict';

  // THE CLOTH ROWS' THREE STEP CUES (2026-10-14, the owner's brief "show the
  // shopper the three steps"; it replaces the two markers of 2026-10-01).
  //
  // On EVERY tile of the two cloth rows, from the moment the tile comes near
  // the screen and before anyone has touched it, three numbered cues stand
  // beside the three controls in the order the card is used:
  //
  //   1  under the size rail             pick your table size
  //   2  over the swatch grid            pick a colour
  //   3  on the BUY button's shoulder    press BUY
  //
  // THE FORM IS THE OVERLAY, NOT INLINE LABELS, and the measurement decided
  // it (2026-10-14, branch preview, Simonis 860 and Championship Invitational
  // at 1920 / 1440 / 430): the card's white gaps are 8px over the size rail,
  // 12 between the photograph and the swatches (6 of flex gap + 6 of margin),
  // 6 over the bottom block and 3 over BUY, and the smallest line of type
  // this theme sets (10px) needs 14. A label in the flow would have grown
  // every tile, which the brief forbids. So the cues hang inside the card
  // (.simonis-family-card is position:relative), positioned from the
  // controls' own rects and never in the flow: tile heights before and after
  // are the same numbers - Simonis 698.4 / 679.2 / 663.9, Championship 670.6 /
  // 653 / 632.1 at 1920 / 1440 / 430.
  //
  // Where each one sits, and why it covers no control:
  //   1 hangs 6px under the rail, on the photograph's top-left corner.
  //   2 stands over the swatch grid, seated on the photograph's own bottom
  //     edge, which is read off the file because the colour's name is printed
  //     across the photograph's foot - and above any lettering printed
  //     inside the cloth itself, which the Rapide photographs carry
  //     (seatOnPhoto below).
  //   3 sits on the BUY button's right shoulder: its bottom 5px on the
  //     button's top edge, right-aligned to the button. The label's line
  //     starts 10.5px below the button's top on the desktop (34px button,
  //     13px line) and 15.5 on the phone (44px), so the cue clears it by 5.5 /
  //     10.5 and covers no letter; above it the cue takes the "+ Pre-Cut
  //     Rails" line's right-hand half, which is empty on every card (that
  //     line is a fixed string of ~99px in a box of 250 or more), and its
  //     top is exactly that line's top - the colour name's line box ends
  //     there, and its descenders reach that edge: measured 0.4px clear on
  //     "Championship Blue" at 1920, 2.6 on the Simonis names. Four pixels
  //     onto the button had the cue a pixel into the "p".
  //
  // THEY GO WHEN THE SHOPPER TOUCHES THAT CARD, and only that card: a TAP or
  // a click anywhere on it, or keyboard focus landing inside it. A tap is a
  // pointer that goes down and comes up within 10px - a finger that lands
  // on the card to scroll the page or swipe the lane is not a decision
  // about the card and leaves its cues alone (without this, every scroll
  // through the rows dismissed whichever card the thumb landed on). A swatch
  // chosen or a size chosen is a tap and counts; so does the whole-card
  // link. Hover does not end them: a pointer passing over the row is not a
  // decision, and the hover preview of a swatch is what cue 2 points at.
  //
  // Once a card's cues are gone they stay gone while the page is open, and
  // nothing is remembered across loads - the once-per-visitor flag of the
  // first two versions (bc-cloth-hint-seen, bc-cloth-hint-dismissed,
  // bc-cloth-tip-done) is what kept the owner from ever seeing them. There
  // is no timer on either surface: the phone's cues wait for the first tap.
  //
  // The cues take no pointer events, so the tap that ends them still lands
  // on the chip, the swatch or the button under it and does what it would
  // have done. A tap meant only to dismiss has nowhere neutral to land on a
  // card whose every pixel is a link; that is the card's design (see
  // assets/bc-card-link.js), not this file's.

  const ROWS = '[data-cloth-family-section="simonis"], [data-cloth-family-section="championship"]';
  const CARD = '.simonis-family-card';
  const ACTIVE_PANEL = '.simonis-family-card__size-panel.is-active';
  const FADE_MS = 200;
  const GAP = 6;
  const BUY_OVERLAP = 5;
  const TAP_SLOP = 10;

  const STEPS = [
    { key: 'size', target: '.simonis-family-card__size-rail', side: 'below', inPanel: false },
    { key: 'colour', target: '.cloth-size-card__variation-thumbnails', side: 'above', inPanel: true },
    { key: 'buy', target: '.cloth-size-card__buy', side: 'shoulder', inPanel: true },
  ];

  const SCRIPT = document.currentScript || document.querySelector('script[src*="bc-cloth-tip"]');
  const TEXT = {
    size: (SCRIPT && SCRIPT.dataset.tipSize) || '',
    colour: (SCRIPT && SCRIPT.dataset.tipColour) || '',
    buy: (SCRIPT && SCRIPT.dataset.tipBuy) || '',
  };

  if (!TEXT.size || !TEXT.colour || !TEXT.buy) return;
  if (!('IntersectionObserver' in window)) return;

  // The control a cue names. The cloth card carries one panel per table
  // size with only the active one shown, so the swatch grid and the BUY
  // button are read from the active panel first; a plain querySelector with
  // two selectors would return the FIRST panel's in document order, hidden
  // or not.
  function control(card, step) {
    if (step.inPanel) {
      const panel = card.querySelector(ACTIVE_PANEL);
      const inActive = panel && panel.querySelector(step.target);

      if (inActive) return inActive;
    }

    return card.querySelector(step.target);
  }

  function build(card, step, index) {
    if (!control(card, step)) return null;

    const tip = document.createElement('div');

    tip.className = 'bc-cloth-tip bc-cloth-tip--' + step.key;
    tip.setAttribute('aria-hidden', 'true');

    const num = document.createElement('span');

    num.className = 'bc-cloth-tip__num';
    num.textContent = String(index + 1);

    const label = document.createElement('span');

    label.className = 'bc-cloth-tip__label';
    label.textContent = TEXT[step.key];

    tip.appendChild(num);
    tip.appendChild(label);
    card.appendChild(tip);

    return { tip, step };
  }

  // Everything is measured against the card, which is the positioned
  // ancestor, so a cue never enters the flow.
  function place(card, record) {
    const target = control(card, record.step);

    if (!target) return;

    const cardBox = card.getBoundingClientRect();
    const box = target.getBoundingClientRect();
    const size = record.tip.getBoundingClientRect();
    const height = size.height || 26;
    let top;
    let left;

    if (record.step.side === 'below') {
      top = box.bottom - cardBox.top + GAP;
      left = box.left - cardBox.left;
    } else if (record.step.side === 'above') {
      top = box.top - cardBox.top - GAP - height;
      left = box.left - cardBox.left;
    } else {
      top = box.top - cardBox.top - height + BUY_OVERLAP;
      left = box.right - cardBox.left - size.width;
    }

    position(card, record.tip, top, left);
  }

  // top/left arrive measured from the card's BORDER box (getBoundingClientRect),
  // and an absolute child is offset from the PADDING box, inside the card's
  // 1px border - so the border is taken off, or every cue lands a pixel low
  // and a pixel right (the first version did, measured 7.2 for a 6px gap).
  function position(card, tip, top, left) {
    const cardBox = card.getBoundingClientRect();
    const size = tip.getBoundingClientRect();
    const limit = cardBox.width - size.width - left;

    tip.style.top = `${Math.round(Math.max(2, Math.min(cardBox.height - size.height - 2, top)) - card.clientTop)}px`;
    tip.style.left = `${Math.round(Math.max(2, limit < 0 ? left + limit : left) - card.clientLeft)}px`;
  }

  // THE PHOTOGRAPH CARRIES ITS COLOUR'S NAME PRINTED ACROSS ITS FOOT, and
  // the 12px between the photograph and the swatches is not enough to clear
  // it, so cue 2 landed across the word - measured on Simonis Green at 1920
  // and at 390, where it cut "SIMONIS GREEN" in half. The name is part of the
  // image and the image is square, so its foot moves with the card's width
  // and no constant offset is right at more than one width. This reads it
  // off the file instead: the lowest row that is mostly ink is the cloth
  // square's own bottom (a swatch fills ~85% of the width, the lettering
  // under it nowhere near 60%), and the cue is seated just above it.
  // Anything that stops this - a photograph served without CORS, a browser
  // without OffscreenCanvas - simply leaves the cue where place() put it.
  async function seatOnPhoto(card, record) {
    const tip = record.tip;
    const panel = card.querySelector(ACTIVE_PANEL) || card;
    const img = panel.querySelector('.cloth-size-card__image');

    if (!img || typeof createImageBitmap !== 'function' || typeof OffscreenCanvas !== 'function') return;

    const box = img.getBoundingClientRect();

    if (!box.height) return;

    // The row-to-pixel mapping below holds only while the file fills its box.
    // Every cloth photograph in this shop is square in a square box, and a
    // letterboxed one would put the seat in the wrong place, so it is left
    // where place() put it instead.
    if (img.naturalWidth && img.naturalHeight) {
      const fileRatio = img.naturalWidth / img.naturalHeight;
      const boxRatio = box.width / box.height;

      if (Math.abs(fileRatio - boxRatio) > 0.02) return;
    }

    const response = await fetch(img.currentSrc || img.src, { mode: 'cors' });

    if (!response.ok) return;

    const bitmap = await createImageBitmap(await response.blob());
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    ctx.drawImage(bitmap, 0, 0);

    const { data } = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    const step = Math.max(1, Math.floor(bitmap.width / 160));
    let bottom = -1;

    for (let y = bitmap.height - 1; y >= 0 && bottom < 0; y -= 1) {
      let ink = 0;
      let seen = 0;

      for (let x = 0; x < bitmap.width; x += step) {
        const i = (y * bitmap.width + x) * 4;

        seen += 1;
        if (data[i + 3] > 24 && (data[i] < 238 || data[i + 1] < 238 || data[i + 2] < 238)) ink += 1;
      }

      if (seen && ink / seen >= 0.6) bottom = y;
    }

    if (bottom < 0) return;

    // LETTERING PRINTED INSIDE THE CLOTH'S FOOT. The Rapide photographs
    // carry "BLUE GREEN" and "DELSA BLUE" across the lowest band of the cloth
    // itself (measured 2026-10-14: the cue sat on "BLUE"), where the other
    // families print the name on the white under the cloth; and the fleece
    // photographs (Invitational, Valley, Mercury) end in a fringe some 12px
    // deep that the ink-row test above counts as cloth. So the band the cue
    // would cover - its own height and width, in the file's pixels, at the
    // cloth's left - is read before it is used, and compared with a band of
    // the same size at the cloth's middle, which is plain cloth on every
    // photograph in the shop. Both are read in 6px blocks, not pixels: at
    // the file's resolution the weave itself is high-contrast (a pixel test
    // called every band of every photograph lettering and walked the cue to
    // the top), where a 6x6 mean flattens the weave and keeps a letter's
    // stroke. The share of blocks more than 25 steps of brightness from the
    // band's median is the measure; the comparison is what makes it hold,
    // because every file carries a white margin at the cloth's left that
    // puts the same ~10% of blocks "far" in every band (measured on all
    // twelve cards: plain bands 10.3-12.1%, the Rapide lettering 17.8-21.3,
    // the fringe 79-92). More than 2 points over the middle band is
    // lettering; the cue steps up 4px at a time until its band is clean,
    // and a photograph that never offers one keeps the foot seat.
    const scale = bitmap.height / box.height;
    const tipBox = tip.getBoundingClientRect();
    const height = tipBox.height || 26;
    const bandH = Math.max(2, Math.round(height * scale));
    const bandW = Math.min(bitmap.width, Math.round(((tipBox.width || 120) + GAP) * scale));
    const BLOCK = 6;
    const farShare = (end) => {
      const start = Math.max(0, end - bandH);
      const blocks = [];

      for (let y = start; y + BLOCK <= end; y += BLOCK) {
        for (let x = 0; x + BLOCK <= bandW; x += BLOCK) {
          let sum = 0;

          for (let yy = y; yy < y + BLOCK; yy += 1) {
            for (let xx = x; xx < x + BLOCK; xx += 1) {
              const i = (yy * bitmap.width + xx) * 4;

              sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            }
          }

          blocks.push(sum / (BLOCK * BLOCK));
        }
      }

      if (blocks.length < 8) return 0;

      const median = blocks.slice().sort((a, b) => a - b)[blocks.length >> 1];
      let far = 0;

      for (const b of blocks) if (Math.abs(b - median) > 25) far += 1;

      return far / blocks.length;
    };
    const plain = farShare(Math.round(bottom - bitmap.height * 0.4));
    const lettered = (end) => farShare(end) - plain > 0.02;

    const first = bottom + 1 - Math.round(GAP * scale);
    let seat = first;
    let guard = 0;

    while (seat > bandH && lettered(seat) && guard < 30) {
      seat -= Math.round(4 * scale);
      guard += 1;
    }

    if (guard >= 30) seat = first;

    // The image is drawn to fit its box, so a row of the file maps to the
    // same fraction of the rendered height. The seat is kept as that
    // fraction, so a resize re-places the cue synchronously from the
    // photograph's current rect (reseat below) instead of fetching and
    // reading the file again and moving the cue twice - measured 2026-10-14:
    // place() wrote the swatch-edge position and the photograph's seat
    // arrived 670ms later, a visible jump on every resize, and a phone fires
    // resize when its URL bar collapses on a scroll. BOTH rects are read in
    // reseat, after the awaits above and at the same moment: the first
    // version read the photograph's rect before the fetch and the card's
    // after it, and a page that scrolled in between - which is exactly when
    // a card gets its cues, 200px before it comes into view - put the
    // scroll's distance into the seat and pinned cue 2 to the card's floor,
    // on BUY (caught in a recording of the served build).
    record.seat = seat / bitmap.height;
    reseat(card, record);
  }

  // Cue 2 from its kept seat, or where place() puts it while there is none.
  function reseat(card, record) {
    if (record.seat === undefined) {
      place(card, record);
      return;
    }

    const panel = card.querySelector(ACTIVE_PANEL) || card;
    const img = panel.querySelector('.cloth-size-card__image');

    if (!img) return;

    const now = img.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    const height = record.tip.getBoundingClientRect().height || 26;
    const foot = now.top + record.seat * now.height;

    position(card, record.tip, foot - cardBox.top - height, now.left - cardBox.left);
  }

  function seatColour(card, records) {
    const colour = records.find((record) => record.step.key === 'colour');

    if (!colour) return Promise.resolve();

    return Promise.race([
      seatOnPhoto(card, colour).catch(() => undefined),
      new Promise((resolve) => window.setTimeout(resolve, 400)),
    ]);
  }

  // Cards whose cues are on screen, and cards that have had theirs. The
  // second set is what keeps a dismissed card dismissed for the rest of the
  // page's life, whatever scrolls back into view.
  const live = new Map();
  const dismissed = new WeakSet();

  function runCard(card) {
    if (live.has(card) || dismissed.has(card)) return;

    const records = STEPS.map((step, index) => build(card, step, index)).filter(Boolean);

    if (!records.length) return;

    live.set(card, records);

    for (const record of records) place(card, record);

    let downAt = null;

    const end = () => {
      if (!live.has(card)) return;

      live.delete(card);
      dismissed.add(card);
      card.removeEventListener('pointerdown', onDown, true);
      card.removeEventListener('pointerup', onUp, true);
      card.removeEventListener('pointercancel', onCancel, true);
      card.removeEventListener('click', end, true);
      card.removeEventListener('focusin', end, true);

      for (const record of records) record.tip.classList.remove('is-in');

      window.setTimeout(() => {
        for (const record of records) record.tip.remove();
      }, FADE_MS);
    };

    // A tap: down and up within TAP_SLOP. The pointer that moved is a
    // scroll or a swipe and is let through untouched.
    const onDown = (event) => {
      downAt = { id: event.pointerId, x: event.clientX, y: event.clientY };
    };

    const onUp = (event) => {
      if (!downAt || downAt.id !== event.pointerId) return;

      const moved = Math.hypot(event.clientX - downAt.x, event.clientY - downAt.y);

      downAt = null;
      if (moved <= TAP_SLOP) end();
    };

    const onCancel = () => {
      downAt = null;
    };

    card.addEventListener('pointerdown', onDown, true);
    card.addEventListener('pointerup', onUp, true);
    card.addEventListener('pointercancel', onCancel, true);
    // The click is the fallback for a pointer the browser did not report as
    // one - a keyboard Enter on a focused control, an assistive click.
    card.addEventListener('click', end, true);
    card.addEventListener('focusin', end, true);

    // The colour cue is seated off the photograph itself, which means a
    // fetch; the fade waits for it, so no cue jumps once drawn. A frame
    // before the class, so the fade has a start to run from.
    seatColour(card, records).then(() => window.requestAnimationFrame(() => {
      if (!live.has(card)) return;
      for (const record of records) record.tip.classList.add('is-in');
    }));
  }

  // The window resized: the controls moved, so the cues follow them - cue 2
  // from its kept seat on the photograph, synchronously.
  let resizeTimer = 0;

  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      for (const [card, records] of live) {
        for (const record of records) {
          if (record.step.key === 'colour') reseat(card, record);
          else place(card, record);
        }
      }
    }, 150);
  });

  // A card gets its cues as it comes within 200px of the screen - in the
  // page's scroll or the lane's - rather than all twelve at once on load,
  // because seating cue 2 reads the photograph and a lazy image off screen
  // would be fetched for nothing else.
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);
        runCard(entry.target);
      }
    }
  }, { threshold: 0, rootMargin: '200px' });

  // THE ROWS ARE NOT THERE WHEN THIS RUNS. Both cloth rows are lazy: the
  // homepage ships a slot and the section arrives later, so a script that
  // looks once - even a deferred one - finds nothing and watches nothing.
  // Measured before this was added: the markers never fired on a first visit
  // and only appeared after a reload, when the cached section beat them.
  const watched = new WeakSet();

  function attach() {
    let rows = 0;

    for (const row of document.querySelectorAll(ROWS)) {
      const cards = row.querySelectorAll(CARD);

      if (!cards.length) continue;

      rows += 1;

      for (const card of cards) {
        if (watched.has(card)) continue;

        watched.add(card);
        observer.observe(card);
      }
    }

    return rows;
  }

  if (attach() < 2) {
    const dom = new MutationObserver(() => {
      if (attach() >= 2) dom.disconnect();
    });

    dom.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => dom.disconnect(), 30000);
  }
})();
